import { useState } from "react";
import { object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { MaxTimeModifiers } from "@repo/utils";
import { useEstimateStructure } from "@tools";
import { TransferWorkload } from "@tools/EstimateEditor/components";
import { Dialog, Button } from "@components";
import { Collapse, Grid, IconButton } from "@material-ui/core";
import { TimelineSettingsHeader } from "./components/TimelineSettingsHeader";
import { TimelineSettingsRow } from "./components/TimelineSettingsRow";
import { Settings } from "@assets";
import classnames from "classnames";

export const TimelineSectionSettings = observer(({ section, workType, teamMember }) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const [ isOpened, open ] = useState(false);
  const [ expandedSections, setExpandedSections ] = useState(new Set());
  const [ expandedWorkTypes, setExpandedWorkTypes ] = useState(new Set());

  const sections = section ? [section] : structure.currentTimeline?.sections || [];
  const elementProvided = Boolean(section || workType || teamMember);
  const showVisibility = !elementProvided;

  const save = (callback) => {
    structure.historyManager.startGroup();
    try {
      callback();
    } finally {
      structure.historyManager.stopGroup();
    }
  };

  const title = teamMember
    ? t("views.editor.dialogs.timeline_settings.team_member_title")
    : workType
      ? t("views.editor.dialogs.timeline_settings.work_type_title")
      : t("views.editor.dialogs.timeline_settings.section_title");

  const renderRow = (item, depth, expansionKey, showArrow) => (
    <TimelineSettingsRow
      item={item}
      depth={depth}
      onColor={(color) => save(() => item.setColor(color))}
      onFontColor={(color) => save(() => item.setFontColor(color))}
      onAllocation={(value) =>
        save(() => item.setTimelineAllocation(value * MaxTimeModifiers.HOUR))
      }
      showVisibility={showVisibility}
      showAllocation={!item.isAutomated || depth === 2}
      showHidden={showVisibility && depth === 0}
      onVisibilityChange={() =>
        save(() => item.setHidden(!item.hidden))
      }
      showExpandButton={expansionKey}
      showArrow={showArrow}
      expanded={expansionKey && (depth === 0 ? expandedSections.has(expansionKey) : expandedWorkTypes.has(expansionKey))}
      onExpand={expansionKey && toggle(depth === 0 ? setExpandedSections : setExpandedWorkTypes, expansionKey)}
    />
  );

  const toggle = (setExpanded, id) => () => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const renderWorkType = (item, sectionId) => {
    const expansionKey = `${sectionId || "work-type"}-${item.id}`;

    return (
      <Grid item key={item.id}>
        <Grid item container alignItems="center" className={classnames(!section && sectionId && "pl-6")}>
          { renderRow(item, 1, !workType && item.teamMembers?.length > 0 && expansionKey, !workType) }
        </Grid>
        <Collapse
          in={Boolean(workType) || expandedWorkTypes.has(expansionKey)}
          className="w-full"
          mountOnEnter
          unmountOnExit
        >
          {
            item.teamMembers?.map((member, index) => (
              <Grid item container direction="column" key={member.id} className={classnames(!sectionId && sectionId ? "pl-12" : "pl-6")}>
                { renderRow(member, 2, undefined, true) }
                {index < item.teamMembers.length - 1 && !member.isAutomated && (
                  <Grid container justifyContent="center">
                    <TransferWorkload workType={item} source={member} target={item.teamMembers[index + 1]} />
                  </Grid>
                )}
              </Grid>
            ))
          }
        </Collapse>
      </Grid>
    );
  };

  const renderSection = (item) => (
    <Grid item key={item.id}>
      <Grid item container alignItems="center">
        { renderRow(item, 0, !section && item.workTypes?.length > 0 && item.id) }
      </Grid>
      <Collapse
        in={Boolean(section) || expandedSections.has(item.id)}
        className="w-full"
        mountOnEnter
        unmountOnExit
      >
        { item.workTypes?.map((workType) => renderWorkType(workType, item.id)) }
      </Collapse>
    </Grid>
  );

  const content = teamMember
    ? renderRow(teamMember, 2)
    : workType
      ? renderWorkType(workType)
      : sections.map(renderSection);

  return (
    <>
      <IconButton
        size="small"
        color="primary"
        onClick={() => open(!isOpened)}
        tabIndex={-1}
        className={classnames("noDrag cellHoverIcon", (teamMember || workType || section) && "text-sm p-0-5")}
      >
        <Settings />
      </IconButton>
      <Dialog
        open={isOpened}
        onClose={() => open(false)}
        title={title}
        width={elementProvided ? 560 : 720}
        actions={
          <Button variant="contained" color="primary" onClick={() => open(false)}>
            {t("common.done")}
          </Button>
        }
      >
        <Grid container direction="column">
          {sections.length > 0 || elementProvided ? (
            <>
              <TimelineSettingsHeader showVisibility={showVisibility} />
              {content}
            </>
          ) : (
            <p>
              {t("views.editor.dialogs.timeline_settings.no_sections")}
            </p>
          )}
        </Grid>
      </Dialog>
    </>
  );
});

TimelineSectionSettings.propTypes = {
  section: object,
  workType: object,
  teamMember: object,
};
